import React, { useState } from 'react'
import { X, Type, Palette, Move } from 'lucide-react'
import { HexColorPicker } from 'react-colorful'
import { DialogBubbleData } from '../types'

interface FloatingPanelProps {
  bubble: DialogBubbleData
  onUpdate: (updates: Partial<DialogBubbleData>) => void
  onClose: () => void
}

export const FloatingPanel: React.FC<FloatingPanelProps> = ({
  bubble,
  onUpdate,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<'text' | 'style' | 'position'>('text')
  const [showColorPicker, setShowColorPicker] = useState<'text' | 'background' | 'border' | null>(null)

  const tabs = [
    { id: 'text' as const, label: '文字', icon: Type },
    { id: 'style' as const, label: '样式', icon: Palette },
    { id: 'position' as const, label: '位置', icon: Move },
  ]

  const fontFamilies = [
    { value: 'Inter', label: 'Inter' },
    { value: 'Arial', label: 'Arial' },
    { value: 'SimHei', label: '黑体' },
    { value: 'SimSun', label: '宋体' },
    { value: 'KaiTi', label: '楷体' },
    { value: 'Microsoft YaHei', label: '微软雅黑' },
  ]

  const bubbleTypes = [
    { value: 'speech' as const, label: '普通对话' },
    { value: 'thought' as const, label: '思考气泡' },
    { value: 'shout' as const, label: '喊叫气泡' },
    { value: 'explode' as const, label: '爆炸气泡' }
  ]

  const renderTextTab = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">文字内容</label>
        <textarea
          value={bubble.text}
          onChange={(e) => onUpdate({ text: e.target.value })}
          className="glass-input w-full h-20 resize-none"
          placeholder="输入对话内容..."
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">字体</label>
        <select
          value={bubble.fontFamily}
          onChange={(e) => onUpdate({ fontFamily: e.target.value })}
          className="glass-input w-full"
        >
          {fontFamilies.map(font => (
            <option key={font.value} value={font.value}>{font.label}</option>
          ))}
        </select>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">字体大小: {bubble.fontSize}px</label>
        <input
          type="range"
          min="10"
          max="32"
          value={bubble.fontSize}
          onChange={(e) => onUpdate({ fontSize: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">文字颜色</label>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowColorPicker(showColorPicker === 'text' ? null : 'text')}
            className="w-8 h-8 rounded border-2 border-gray-300"
            style={{ backgroundColor: bubble.color }}
          />
          <span className="text-sm font-mono">{bubble.color}</span>
        </div>
        {showColorPicker === 'text' && (
          <div className="mt-2">
            <HexColorPicker
              color={bubble.color}
              onChange={(color) => onUpdate({ color })}
            />
          </div>
        )}
      </div>
    </div>
  )

  const renderStyleTab = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">气泡类型</label>
        <div className="grid grid-cols-2 gap-2">
          {bubbleTypes.map(type => (
            <button
              key={type.value}
              onClick={() => onUpdate({ type: type.value })}
              className={`p-2 rounded-lg text-sm transition-colors ${
                bubble.type === type.value
                  ? 'bg-primary-100 border-primary-300 text-primary-700'
                  : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
              } border`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>
      {/* 箭头方向切换，仅普通/喊叫气泡显示 */}
      {(bubble.type === 'speech' || bubble.type === 'shout') && (
        <div>
          <label className="block text-sm font-medium mb-2">箭头方向</label>
          <div className="flex space-x-2">
            <button
              onClick={() => onUpdate({ arrowDirection: 'down' })}
              className={`px-3 py-1 rounded border ${bubble.arrowDirection === 'down' ? 'bg-primary-100 border-primary-400' : 'border-gray-300'}`}
            >
              <span role="img" aria-label="朝下">⬇️ 朝下</span>
            </button>
            <button
              onClick={() => onUpdate({ arrowDirection: 'up' })}
              className={`px-3 py-1 rounded border ${bubble.arrowDirection === 'up' ? 'bg-primary-100 border-primary-400' : 'border-gray-300'}`}
            >
              <span role="img" aria-label="朝上">⬆️ 朝上</span>
            </button>
          </div>
        </div>
      )}
      
      <div>
        <label className="block text-sm font-medium mb-2">背景颜色</label>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowColorPicker(showColorPicker === 'background' ? null : 'background')}
            className="w-8 h-8 rounded border-2 border-gray-300"
            style={{ backgroundColor: bubble.backgroundColor }}
          />
          <span className="text-sm font-mono">{bubble.backgroundColor}</span>
        </div>
        {showColorPicker === 'background' && (
          <div className="mt-2">
            <HexColorPicker
              color={bubble.backgroundColor}
              onChange={(color) => onUpdate({ backgroundColor: color })}
            />
          </div>
        )}
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">边框颜色</label>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowColorPicker(showColorPicker === 'border' ? null : 'border')}
            className="w-8 h-8 rounded border-2 border-gray-300"
            style={{ backgroundColor: bubble.borderColor }}
          />
          <span className="text-sm font-mono">{bubble.borderColor}</span>
        </div>
        {showColorPicker === 'border' && (
          <div className="mt-2">
            <HexColorPicker
              color={bubble.borderColor}
              onChange={(color) => onUpdate({ borderColor: color })}
            />
          </div>
        )}
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">边框宽度: {bubble.borderWidth}px</label>
        <input
          type="range"
          min="1"
          max="6"
          value={bubble.borderWidth}
          onChange={(e) => onUpdate({ borderWidth: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">圆角: {bubble.borderRadius}px</label>
        <input
          type="range"
          min="0"
          max="32"
          value={bubble.borderRadius}
          onChange={(e) => onUpdate({ borderRadius: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
    </div>
  )

  const renderPositionTab = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">X位置</label>
          <input
            type="number"
            value={bubble.x}
            onChange={(e) => onUpdate({ x: parseInt(e.target.value) || 0 })}
            className="glass-input w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Y位置</label>
          <input
            type="number"
            value={bubble.y}
            onChange={(e) => onUpdate({ y: parseInt(e.target.value) || 0 })}
            className="glass-input w-full"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">宽度</label>
          <input
            type="number"
            value={bubble.width}
            onChange={(e) => onUpdate({ width: parseInt(e.target.value) || 60 })}
            className="glass-input w-full"
            min="60"
            max="300"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">高度</label>
          <input
            type="number"
            value={bubble.height}
            onChange={(e) => onUpdate({ height: parseInt(e.target.value) || 40 })}
            className="glass-input w-full"
            min="40"
            max="200"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">内边距: {bubble.padding}px</label>
        <input
          type="range"
          min="4"
          max="20"
          value={bubble.padding}
          onChange={(e) => onUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">旋转角度: {bubble.rotation}°</label>
        <input
          type="range"
          min="-45"
          max="45"
          value={bubble.rotation}
          onChange={(e) => onUpdate({ rotation: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-2">序号</label>
        <div className="flex items-center space-x-2">
          <input
            type="number"
            value={bubble.sequence || ''}
            onChange={(e) => onUpdate({ sequence: e.target.value ? parseInt(e.target.value) : null })}
            className="glass-input flex-1"
            placeholder="不设置序号"
            min="1"
          />
          <button
            onClick={() => onUpdate({ sequence: null })}
            className="glass-button text-sm"
          >
            清除
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="floating-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gradient">属性面板</h3>
        <button
          onClick={onClose}
          className="w-6 h-6 hover:bg-gray-100 rounded-full flex items-center justify-center"
        >
          <X size={16} />
        </button>
      </div>
      
      {/* 标签页 */}
      <div className="flex space-x-1 mb-4">
        {tabs.map(tab => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary-100 text-primary-700'
                  : 'hover:bg-gray-100'
              }`}
            >
              <Icon size={16} />
              <span>{tab.label}</span>
            </button>
          )
        })}
      </div>
      
      {/* 内容区 */}
      <div className="max-h-96 overflow-y-auto">
        {activeTab === 'text' && renderTextTab()}
        {activeTab === 'style' && renderStyleTab()}
        {activeTab === 'position' && renderPositionTab()}
      </div>
    </div>
  )
} 